import type { ReactNode } from 'react';
import { tokens } from '@fluentui/react-components';
import {
  AppsListDetail20Regular,
  ApprovalsApp20Regular,
  CalendarClock20Regular,
  CheckmarkCircle20Regular,
  DocumentEdit20Regular,
  Warning20Regular,
} from '@fluentui/react-icons';

import { DASHBOARD_DUE_SOON_LABEL } from '../dashboard.constants';
import { EDashboardSegmentKey } from '../types/dashboard.types';
import type { TDashboardMetric, TDashboardSegmentView, TDashboardViewCounters } from '../types/dashboard.types';

type TDashboardSegmentPresentation = {
  title: string;
  caption: string;
  tone: TDashboardMetric['tone'];
  emptyMessage: string;
  icon: ReactNode;
};

const DASHBOARD_SEGMENT_ORDER = [
  EDashboardSegmentKey.OPEN_APPROVAL,
  EDashboardSegmentKey.DRAFT,
  EDashboardSegmentKey.NOT_APPROVED,
  EDashboardSegmentKey.APPROVED,
  EDashboardSegmentKey.OVERDUE_REVIEWS,
  EDashboardSegmentKey.REVIEWS_DUE_SOON,
] as const;

const DASHBOARD_SEGMENT_COUNTER_KEY = {
  [EDashboardSegmentKey.OPEN_APPROVAL]: 'openApprovals',
  [EDashboardSegmentKey.DRAFT]: 'draft',
  [EDashboardSegmentKey.NOT_APPROVED]: 'notApproved',
  [EDashboardSegmentKey.APPROVED]: 'approved',
  [EDashboardSegmentKey.OVERDUE_REVIEWS]: 'overdueReviews',
  [EDashboardSegmentKey.REVIEWS_DUE_SOON]: 'reviewsDueSoon',
} as const satisfies Record<EDashboardSegmentKey, keyof TDashboardViewCounters>;

const DASHBOARD_SEGMENT_PRESENTATION: Record<EDashboardSegmentKey, TDashboardSegmentPresentation> = {
  [EDashboardSegmentKey.OPEN_APPROVAL]: {
    title: 'Open approval',
    caption: 'awaiting approval',
    tone: 'warning',
    emptyMessage: 'No open approval items.',
    icon: <ApprovalsApp20Regular />,
  },
  [EDashboardSegmentKey.DRAFT]: {
    title: 'Draft',
    caption: 'open draft',
    tone: 'neutral',
    emptyMessage: 'No draft items.',
    icon: <DocumentEdit20Regular />,
  },
  [EDashboardSegmentKey.NOT_APPROVED]: {
    title: 'Not approved',
    caption: 'not approved/released',
    tone: 'info',
    emptyMessage: 'No items that are not approved.',
    icon: <AppsListDetail20Regular />,
  },
  [EDashboardSegmentKey.APPROVED]: {
    title: 'Approved',
    caption: 'approved/released',
    tone: 'success',
    emptyMessage: 'No approved items.',
    icon: <CheckmarkCircle20Regular />,
  },
  [EDashboardSegmentKey.OVERDUE_REVIEWS]: {
    title: 'Overdue reviews',
    caption: 'periodic review overdue',
    tone: 'danger',
    emptyMessage: 'No items with overdue reviews.',
    icon: <Warning20Regular />,
  },
  [EDashboardSegmentKey.REVIEWS_DUE_SOON]: {
    title: 'Reviews due soon',
    caption: `within ${DASHBOARD_DUE_SOON_LABEL}`,
    tone: 'warning',
    emptyMessage: 'No items with reviews due soon.',
    icon: <CalendarClock20Regular />,
  },
};

export const getDashboardSegmentLabel = (segmentKey: EDashboardSegmentKey): string =>
  DASHBOARD_SEGMENT_PRESENTATION[segmentKey].title;

export const getDashboardSegmentEmptyMessage = (segmentKey: EDashboardSegmentKey): string =>
  DASHBOARD_SEGMENT_PRESENTATION[segmentKey].emptyMessage;

export const buildDashboardMetrics = (counters: TDashboardViewCounters): TDashboardMetric[] =>
  DASHBOARD_SEGMENT_ORDER.map((segmentKey) => {
    const { title, caption, tone } = DASHBOARD_SEGMENT_PRESENTATION[segmentKey];
    return { segmentKey, title, caption, tone, value: counters[DASHBOARD_SEGMENT_COUNTER_KEY[segmentKey]] };
  });

export const getDashboardMetricIcon = (metric: TDashboardMetric): ReactNode =>
  DASHBOARD_SEGMENT_PRESENTATION[metric.segmentKey].icon;

export const buildDashboardSegmentSummaries = (counters: TDashboardViewCounters): TDashboardSegmentView[] =>
  DASHBOARD_SEGMENT_ORDER.map((key) => {
    const { title } = DASHBOARD_SEGMENT_PRESENTATION[key];
    return { key, label: title, totalCount: counters[DASHBOARD_SEGMENT_COUNTER_KEY[key]] };
  });

type TDashboardPeriodicReviewDatum = {
  label: string;
  value: number;
  color: string;
};

export const buildDashboardPeriodicReviewData = (counters: TDashboardViewCounters): TDashboardPeriodicReviewDatum[] => {
  const onTrack = Math.max(counters.reviewActive - counters.overdueReviews - counters.reviewsDueSoon, 0);

  return [
    {
      label: 'Overdue reviews',
      value: counters.overdueReviews,
      color: tokens.colorPaletteRedForeground1,
    },
    {
      label: `Due within ${DASHBOARD_DUE_SOON_LABEL}`,
      value: counters.reviewsDueSoon,
      color: tokens.colorPaletteMarigoldForeground2,
    },
    { label: 'On track', value: onTrack, color: tokens.colorPaletteGreenForeground1 },
  ];
};
